import { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import { parseCatalogSnapshot } from "./catalog";
import type { CatalogSnapshotV1 } from "./types";

/**
 * The published catalog comes from Convex (public/catalogSnapshots.ts:get),
 * read once and then kept current over the same client's subscription.
 *
 * The production deployment is baked in, as apps/form does: a Convex URL is
 * public, and a build with no variables attached must still work.
 */
const convexUrl =
  (import.meta.env?.VITE_CONVEX_URL as string | undefined) ??
  "https://jovial-bison-730.convex.cloud";

/** Catalog photos are served, resized, by the dashboard's image Worker. */
const imageOrigin = (
  (import.meta.env?.VITE_CATALOG_IMAGE_ORIGIN as string | undefined) ??
  "https://usesela.com"
).replace(/\/+$/, "");

const getSnapshot = makeFunctionReference<
  "query",
  { slug: string },
  { revision: number; payload: string } | null
>("public/catalogSnapshots:get");

let client: ConvexClient | null = null;
function convex(): ConvexClient {
  client ??= new ConvexClient(convexUrl);
  return client;
}

/** The URL of a catalog photo `width` pixels wide (the Worker snaps it). */
export function catalogImageUrl(fileId: string, width: number): string {
  return `${imageOrigin}/api/catalog/images/${encodeURIComponent(fileId)}?w=${width}`;
}

export async function loadCatalog(slug: string): Promise<CatalogSnapshotV1> {
  const row = await convex().query(getSnapshot, { slug });
  // live-catalog.ts reads `code: 404` as "this catalog is not published".
  if (!row) throw { code: 404 };
  return parseCatalogSnapshot(row.payload);
}

export async function subscribeToCatalog(
  slug: string,
  onSnapshot: (snapshot: CatalogSnapshotV1) => void,
  onDelete: () => void,
): Promise<() => void> {
  const unsubscribe = convex().onUpdate(getSnapshot, { slug }, (row) => {
    if (!row) {
      onDelete();
      return;
    }
    try {
      onSnapshot(parseCatalogSnapshot(row.payload));
    } catch {
      // A payload this build cannot read leaves the last good one on screen.
    }
  });
  return () => unsubscribe();
}
